import React from "react";
import _ from "lodash";
import { FormattedMessage } from "react-intl";

import { useForm } from "@sportal/cdk/form/Form";
import { CheckCodeField } from "./supportingComponents/supportingComponents/CheckCodeField";
import {
  DeviceNameInput,
  ProfileSelect,
} from "../../../../components/devices/commonComponents";
import {
  CommonModalFooter,
  CommonModalHeader,
  ModalDialog,
  ModalService,
} from "../../../../components/modal";
import { useIntlTranslate } from "../../../../hooks";
import {
  getLogicalDevicesEntities,
  saveDevice,
} from "../../../../store/devices/logicalDevices";
import { getMappedProfilesForSelect } from "../../../../store/profiles/profiles.selectors";
import { SBLogicalDevice } from "../../../../store/devices/devices.types";
import { ProfileForSelect } from "../../../../store/profiles/profiles.types";

const CLASS_NAME = "add-device-modal--by-code";

interface OpenDeviceCreationByCodeDialogProps {
  currentProfile: string;
}
interface AddDeviceByCodeDialogProps {
  modal: any;
  allDevices: Record<string, SBLogicalDevice>;
  currentProfile: string;
  profiles: ProfileForSelect[];
}

export const openDeviceCreationByCodeDialog =
  ({ currentProfile }: OpenDeviceCreationByCodeDialogProps) =>
  (dispatch, getState) => {
    const state = getState();
    const allDevices = getLogicalDevicesEntities(state);
    const profiles = getMappedProfilesForSelect(state);

    ModalService.show((modal) => ({
      dialog: (
        <AddDeviceByCodeDialog
          modal={modal}
          allDevices={allDevices}
          currentProfile={currentProfile}
          profiles={profiles}
        />
      ),
    }))
      .then((device) => dispatch(saveDevice(device)))
      .catch(_.noop);
  };

const AddDeviceByCodeDialog = ({
  modal,
  allDevices,
  currentProfile,
  profiles,
}: AddDeviceByCodeDialogProps) => {
  const translate = useIntlTranslate();
  const formProps = useForm({ address: "", name: "", profile: currentProfile });

  const close = () => modal.reject();
  const submit = () =>
    modal.resolve({
      identifiers: [formProps.values.address],
      name: formProps.values.name,
      profile: formProps.values.profile,
    });

  const handleDeviceSearchProgress = () => {
    formProps.set("address", "");
    formProps.setError("address", "empty");
  };
  const handleDeviceSearchNotFound = () => {
    formProps.set("address", "");
    formProps.setError("address", "not_found");
  };
  const handleDeviceSearchSuccess = (address: string) => {
    formProps.set("address", address);
    formProps.setError("address", false);
  };
  const handleDeviceNameChange = (name: string, error: string) => {
    formProps.set("name", name);
    formProps.setError("name", error);
  };
  const handleProfileChange = (profile: string) => {
    formProps.set("profile", profile);
  };

  const isSubmitDisabled =
    !formProps.didFormChange() ||
    formProps.hasErrors() ||
    !formProps.values.name ||
    !formProps.values.address;

  return (
    <ModalDialog
      className={CLASS_NAME}
      rejectHandler={close}
      submitHandler={submit}
      disabled={isSubmitDisabled}
    >
      <CommonModalHeader>
        <FormattedMessage id={"add_device_by_code"} />
      </CommonModalHeader>
      <ModalDialog.Body>
        <section
          className={"section section--search"}
          aria-labelledby={"section-header-search"}
        >
          <div className={"section__indicator"}>1</div>
          <div className="section__content">
            <header className={"section__header"} id={"section-header-search"}>
              {translate("add_device_by_code_section_find_code")}
            </header>
            <CheckCodeField
              allDevices={allDevices}
              onDeviceSearchNotFound={handleDeviceSearchNotFound}
              onDeviceSearchSuccess={handleDeviceSearchSuccess}
              onDeviceSearchProgress={handleDeviceSearchProgress}
            />
          </div>
        </section>

        <section
          className={"section section--assign"}
          aria-labelledby={"section-header-assign"}
        >
          <div className={"section__indicator"}>2</div>
          <div className="section__content">
            <header className={"section__header"} id={"section-header-assign"}>
              {translate("add_device_by_code_section_assign")}
            </header>
            <DeviceNameInput
              label={translate("device_name")}
              devices={allDevices}
              name={formProps.values.name}
              handleChange={handleDeviceNameChange}
              error={formProps.errors.name && translate(formProps.errors.name)}
            />
            <ProfileSelect
              profiles={profiles}
              profile={formProps.values.profile}
              handleChange={handleProfileChange}
              className={CLASS_NAME}
            />
          </div>
        </section>
      </ModalDialog.Body>
      <CommonModalFooter submitMessage={translate("add_device")} />
    </ModalDialog>
  );
};
